/**
 * When may Val / Norma call GET /codebook-tree?
 *
 * Pilot users (delegated SharePoint) must wait for both the session id_token
 * (so apiClient auth is ready) and a live SharePoint access token. Fetching
 * earlier hits the app-only / blob fallback and locks the UI on opaque
 * Graph-style folder ids under a "BLOB STORAGE" badge until a manual refresh
 * (staging repro 2026-10-23).
 *
 * Non-pilot users use app-only Graph (AZURE_AD_CLIENT_ID) or blob — no
 * delegated token required.
 */

export type CodebookTreeFetchDecision =
  | { action: 'fetch'; requireSharePointToken: boolean }
  | { action: 'defer'; reason: 'wait-id-token' | 'wait-sharepoint-token' }

/**
 * Single definition of "is this the delegated-auth pilot user". Trim and
 * case-fold both sides so every caller (mount effect, token getter, fetch
 * decision) agrees on who is a pilot.
 */
export function isSharePointPilotUser(
  pilotEmail: string | undefined | null,
  userEmail: string | undefined | null,
): boolean {
  const pilot = (pilotEmail ?? '').trim().toLowerCase()
  const email = (userEmail ?? '').trim().toLowerCase()
  return !!pilot && !!email && email === pilot
}

export function decideCodebookTreeFetch(opts: {
  pilotEmail: string | undefined | null
  userEmail: string | undefined | null
  sessionIdToken: string | undefined | null
  sharePointToken: string | undefined | null
}): CodebookTreeFetchDecision {
  const isPilot = isSharePointPilotUser(opts.pilotEmail, opts.userEmail)

  if (!isPilot) {
    return { action: 'fetch', requireSharePointToken: false }
  }
  if (!opts.sessionIdToken) {
    return { action: 'defer', reason: 'wait-id-token' }
  }
  if (!opts.sharePointToken) {
    return { action: 'defer', reason: 'wait-sharepoint-token' }
  }
  return { action: 'fetch', requireSharePointToken: true }
}
